import React, { useRef, useState } from 'react';
import { router } from '@inertiajs/react';
import api, { ApiError } from '../../lib/api';
import type { WikiPageArticle } from '../../types/wiki';

// 出处行旁边的标签编辑。就地编辑，**不进正文编辑态**：§6.3 说版本不追踪标签，
// 把改标签和改正文绑在同一次「保存」上就是把两件不同的事绑在一起；而「补一个
// 标签」是 5 秒的动作，为它走「进编辑态 → 保存 → 完成」太重（§6.7 论证过的
// 同一个形状 —— 腐烂的成因是成本差）。
//
// 交互照 DetailPane 的备注卡片：点一下进输入框、Esc 回滚。
type TagEditorProps = {
  page: WikiPageArticle;
  // 本人用过的标签，给原生 datalist 当候选。由 Web::WikisController#show 出，
  // 与列表页那个标签筛用的是同一份 available_tags。
  allTags: string[];
};

// 逗号分隔，半角与全角都认 —— 中文输入法下打出来的是「，」，而一个把
// 「kamal，部署」存成单个标签的输入框，用户第二次就不会再用它。
//
// 顺手去重、去空白：这两件事不做的话，第一个手滑打出的 "kamal, kamal" 会让
// 列表页那个标签筛出现两个一模一样的按钮。
const parse = (text: string): string[] =>
  Array.from(
    new Set(
      text
        .split(/[,，]/)
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0),
    ),
  );

const same = (a: string[], b: string[]) =>
  a.length === b.length && a.every((tag, index) => tag === b[index]);

const TagEditor: React.FC<TagEditorProps> = ({ page, allTags }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Esc 之后紧跟着的那次 blur 不该再存一遍 —— 否则「取消」和「失焦保存」
  // 会在同一次按键里打起来，而赢的那个是保存。
  const cancelled = useRef(false);

  const open = () => {
    // 每次进编辑态都从服务端那一份重新拼，不留上一次的残留。用「，」拼是因为
    // 中文标签之间打全角逗号更顺手，而 parse 两种都认。
    setText(page.tags.join('，'));
    setError(null);
    cancelled.current = false;
    setEditing(true);
  };

  const save = async () => {
    const tags = parse(text);
    // 没变就直接退出，不发请求。改标签不写版本，但一次没有任何变化的 PATCH
    // 仍然会动 updated_at，而那一列是列表页「最久没动的 10 页」的排序依据 ——
    // 点开一个标签框再关掉，不该让这一页看起来「刚被维护过」。
    if (same(tags, page.tags)) {
      setEditing(false);
      return;
    }

    setSaving(true);
    try {
      // 不带 base_version：乐观锁保护的是「正文被覆盖」（§6.6），而改标签不
      // 覆盖正文，为它回 409 只会让人一脸问号。
      await api.wikiPages.update(page.url_key, { tags });
      setEditing(false);
      // 让服务端把这三个 prop 重新出一遍：标签本身、datalist 的候选（刚打的新
      // 标签下一次就该出现在候选里），以及 wikiNav。
      //
      // ⚠️ wikiNav 必须显式点名。它是**共享** prop，而 inertia_rails 的局部
      // 重载按 dot 路径过滤，共享 props 一视同仁 —— 不写它就会被整块滤掉，
      // 于是左边栏的标签云停在旧的一份上（新标签不出现、删掉的标签还在），
      // 而页面上的标签已经变了。两处显示同一件事而结果不同。
      router.reload({ only: ['page', 'allTags', 'wikiNav'] });
    } catch (e) {
      // 留在编辑态：错误几乎总是「标签太多 / 太长」（WikiPage 那两条校验），
      // 而那两种都是把输入框里的东西改一改就能过的。
      setError(e instanceof ApiError ? e.message : '标签没存上');
    } finally {
      setSaving(false);
    }
  };

  // 阅读态是一排 chip（设计稿的 .chip），点任意一个进输入框。
  //
  // 整排共用一个点击区而不是每个标签各自可点：这一期标签只能整批改（输入框收
  // 逗号分隔的一串），给每个 chip 自己的点击目标会让人以为点它是「筛这个标签」
  // 或者「删这个标签」—— 两个都不是。
  if (!editing) {
    return (
      <button type="button" onClick={open} className="flex items-center gap-1.5 text-left">
        {page.tags.map((tag) => (
          <span
            key={tag}
            className="rounded bg-todo-card2 px-[7px] py-px text-[12px] text-todo-ink2
                       hover:bg-wiki-sideHover"
          >
            {tag}
          </span>
        ))}
        <span className="rounded px-[7px] py-px text-[12px] text-todo-muted hover:bg-todo-card2 hover:text-todo-ink2">
          {page.tags.length > 0 ? '＋ 标签' : '＋ 加标签'}
        </span>
      </button>
    );
  }

  return (
    <span className="flex items-baseline gap-2">
      <input
        list="wiki-tag-options"
        value={text}
        autoFocus
        disabled={saving}
        onChange={(event) => setText(event.target.value)}
        onBlur={() => {
          if (!cancelled.current) save();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            save();
          } else if (event.key === 'Escape') {
            cancelled.current = true;
            setEditing(false);
          }
        }}
        placeholder="标签，逗号分隔"
        className="w-[220px] rounded border border-todo-line bg-todo-card px-1.5 py-0.5
                   text-[12px] text-todo-ink outline-none placeholder:text-todo-muted
                   disabled:opacity-50"
      />
      {/* 原生 datalist，零组件成本。防的是「kamal / Kamal / Kamal部署」这种
          拼写分裂 —— 一旦分裂，列表页那个标签筛就废了。 */}
      <datalist id="wiki-tag-options">
        {allTags.map((tag) => (
          <option key={tag} value={tag} />
        ))}
      </datalist>
      {saving && <span className="text-[12px] text-todo-muted">存标签…</span>}
      {error && <span className="text-[12px] text-todo-urgent">{error}</span>}
    </span>
  );
};

export default TagEditor;
